/**
 * The scheduler's single filter node: one bar that holds every constraint as a
 * removable chip -- typed terms (``artist:wizkid``, ``bpm:100-115``, free words)
 * and picked filters alike -- with a compact drop-down for the multi-selects.
 *
 * Typing works as in the Library: the query language, parsed by the one parser.
 * A term becomes a chip when Space or Enter completes it; Backspace in the empty
 * field takes the last chip back. Clicking the bar opens the drop-down: Eras
 * (decades of the year tag) and Genres as
 * checkboxes, plus the BPM / Energy / Danceability / LUFS range sliders. There is
 * no second artist field: type ``artist:`` or click an artist on a card.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { fetchScheduleFacets, type GenreFacet, type ScheduleFacets } from "../api";
import { RangeSlider } from "../search/FilterPanel";
import { BOUNDS, EMPTY_FILTERS, filterChips, isActive, type CustomFilter, type Era, type Filters } from "../search/filters";
import type { Query } from "../search/parse";

const FIELD_LABEL: Record<string, string> = {
  artist: "Artist", title: "Title", album: "Album", bpm: "BPM", key: "Key", genre: "Genre", subgenre: "Subgenre",
  energylevel: "Energy", energy: "Energy (raw)", danceability: "Dance", year: "Year", lufs: "Loudness", group: "Group",
  duration: "Length",
};

const OPS: Record<string, string> = { gt: ">", gte: "≥", lt: "<", lte: "≤" };

/** A term is complete when it is not inside an open quote. */
function complete(text: string): boolean {
  return (text.match(/"/g)?.length ?? 0) % 2 === 0 && text.trim().length > 0;
}

export function QueryMatrix({
  input,
  setInput,
  typedQuery,
  filters,
  setFilters,
  offline,
  genres,
  count,
  loading,
  columns = [],
}: {
  input: string;
  setInput: (value: string) => void;
  typedQuery: Query;
  filters: Filters;
  setFilters: (next: Filters) => void;
  offline: boolean;
  genres: GenreFacet[];
  count: number | null;
  loading: boolean;
  /** The station's own columns, offered as filters. Dates are not filterable yet. */
  columns?: { key: string; label: string; type: string }[];
}) {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [facets, setFacets] = useState<ScheduleFacets>({ eras: [], rotations: [] });
  const [genreFilter, setGenreFilter] = useState("");
  const root = useRef<HTMLDivElement | null>(null);
  const field = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open || offline) return;
    void fetchScheduleFacets().then(setFacets);
  }, [open, offline]);

  // Close on a click anywhere else.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const commit = () => {
    if (!complete(draft)) return;
    setInput(`${input} ${draft.trim()}`.trim());
    setDraft("");
  };

  const removeRange = (start: number, end: number) =>
    setInput((input.slice(0, start) + input.slice(end)).replace(/\s+/g, " ").trim());

  const chips = filterChips(filters);
  const typed = [
    ...typedQuery.clauses.map((c) => ({
      key: `c${c.token.start}`,
      start: c.token.start,
      end: c.token.end,
      negated: c.negated,
      label: FIELD_LABEL[c.field] ?? c.field,
      value: c.cmp === "range" ? `${c.value}–${c.value2}` : `${OPS[c.cmp as string] ?? ""}${c.subtree ? `${c.value}/*` : String(c.value)}`,
    })),
    ...typedQuery.free.map((f) => ({
      key: `f${f.token.start}`, start: f.token.start, end: f.token.end, negated: f.negated, label: "", value: `“${f.text}”`,
    })),
  ].sort((a, b) => a.start - b.start);

  const removeLast = () => {
    if (chips.length) setFilters(chips[chips.length - 1].remove(filters));
    else if (typed.length) removeRange(typed[typed.length - 1].start, typed[typed.length - 1].end);
  };

  const toggle = <T,>(list: T[] | undefined, item: T, same: (a: T, b: T) => boolean): T[] =>
    (list ?? []).some((x) => same(x, item)) ? (list ?? []).filter((x) => !same(x, item)) : [...(list ?? []), item];

  const shownGenres = useMemo(() => {
    const needle = genreFilter.trim().toLowerCase();
    return genres.filter((g) => !needle || g.name.toLowerCase().includes(needle)).slice(0, 60);
  }, [genres, genreFilter]);

  const anything = typed.length > 0 || chips.length > 0;

  return (
    <div className={`qm ${open ? "open" : ""}`} ref={root}>
      <div
        className={`qm-bar ${typedQuery.errors.length ? "invalid" : ""}`}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) e.preventDefault();
          setOpen(true);
          field.current?.focus();
        }}
      >
        <span className="qm-icon">⌕</span>
        {typed.map((t) => (
          <span key={t.key} className={`qm-chip typed ${t.negated ? "negated" : ""}`}>
            {t.negated && <b>not </b>}
            {t.label && <b>{t.label}: </b>}
            {t.value}
            <button title="Remove" onMouseDown={(e) => e.stopPropagation()} onClick={() => removeRange(t.start, t.end)}>×</button>
          </span>
        ))}
        {chips.map((c) => (
          <span key={c.key} className="qm-chip">
            <b>{c.label}: </b>{c.value}
            <button title="Remove" onMouseDown={(e) => e.stopPropagation()} onClick={() => setFilters(c.remove(filters))}>×</button>
          </span>
        ))}
        {typedQuery.errors.map((e, i) => (
          <span key={`e${i}`} className="qm-chip error" title={e.text}>⚠ {e.message}</span>
        ))}
        <input
          ref={field}
          value={draft}
          spellCheck={false}
          aria-label="Filter the pool"
          placeholder={anything ? "" : "Search by artist, title or album, or click to pick genres, decades, BPM…"}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            const next = e.target.value;
            // A space after a complete term turns it into a chip.
            if (next.endsWith(" ") && complete(next)) {
              setInput(`${input} ${next.trim()}`.trim());
              setDraft("");
            } else setDraft(next);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Backspace" && draft === "") {
              removeLast();
            } else if (e.key === "Escape") {
              setOpen(false);
              field.current?.blur();
            }
          }}
          onBlur={commit}
        />
        <span className="qm-count">{loading ? "…" : count ?? ""}</span>
        {anything && (
          <button className="qm-clear" title="Clear every filter" onMouseDown={(e) => e.stopPropagation()}
            onClick={() => {
              setInput("");
              setDraft("");
              setFilters(EMPTY_FILTERS);
            }}>
            clear
          </button>
        )}
      </div>

      {open && (
        <div className="qm-drop" role="dialog" aria-label="Filters">
          <div className="qm-cols">
            <fieldset>
              <legend>Decade</legend>
              {facets.eras.length === 0 && <span className="qm-none">no year tags</span>}
              {facets.eras.map((era) => {
                const on = (filters.eras ?? []).some((e) => e.label === era.label);
                return (
                  <label key={era.label}>
                    <input type="checkbox" checked={on} onChange={() => setFilters({
                      ...filters,
                      eras: toggle<Era>(filters.eras, { label: era.label, from: era.from, to: era.to }, (a, b) => a.label === b.label),
                    })} />
                    {era.label}<em>{era.tracks}</em>
                  </label>
                );
              })}
            </fieldset>
            <fieldset className="qm-genres">
              <legend>
                Genre
                <input value={genreFilter} placeholder="find…" onChange={(e) => setGenreFilter(e.target.value)} />
              </legend>
              <div className="qm-scroll">
                {shownGenres.map((g) => (
                  <label key={g.name}>
                    <input type="checkbox" checked={(filters.genres ?? []).includes(g.name)}
                      onChange={() => setFilters({ ...filters, genres: toggle(filters.genres, g.name, (a, b) => a === b) })} />
                    {g.name}<em>{g.tracks}</em>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
          {columns.some((c) => c.type !== "date") && (
            <fieldset className="qm-custom">
              <legend>Your columns</legend>
              <div className="qm-custom-grid">
                {columns.filter((c) => c.type !== "date").map((c) => (
                  <CustomColumnFilter key={c.key} column={c} filters={filters} setFilters={setFilters} />
                ))}
              </div>
            </fieldset>
          )}
          <div className="qm-ranges">
            <RangeSlider label="BPM" bounds={BOUNDS.bpm} step={1} value={filters.bpm}
              format={(v) => String(Math.round(v))} onChange={(bpm) => setFilters({ ...filters, bpm })} />
            <RangeSlider label="Energy" bounds={BOUNDS.energyLevel} step={1} value={filters.energyLevel}
              format={(v) => String(v)} onChange={(energyLevel) => setFilters({ ...filters, energyLevel })} />
            <RangeSlider label="Dance" bounds={BOUNDS.danceability} step={0.05} value={filters.danceability}
              format={(v) => v.toFixed(2)} onChange={(danceability) => setFilters({ ...filters, danceability })} />
            <RangeSlider label="Loudness" bounds={BOUNDS.lufs} step={0.5} value={filters.lufs ?? null}
              format={(v) => v.toFixed(1)} onChange={(lufs) => setFilters({ ...filters, lufs })} />
          </div>
          <div className="qm-foot">
            <span>{isActive(filters) ? "Songs must match everything you picked and typed." : "Within a box, any ticked value matches."}</span>
            <button className="ghost" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** One curator column as a filter: yes/no, a min–max, or any of some words. */
function CustomColumnFilter({
  column,
  filters,
  setFilters,
}: {
  column: { key: string; label: string; type: string };
  filters: Filters;
  setFilters: (next: Filters) => void;
}) {
  const current = (filters.custom ?? []).find((c) => c.key === column.key);
  const [text, setText] = useState(current?.type === "text" ? current.value.join(", ") : "");
  const put = (next: CustomFilter | null) => {
    const others = (filters.custom ?? []).filter((c) => c.key !== column.key);
    setFilters({ ...filters, custom: next ? [...others, next] : others });
  };

  if (column.type === "boolean") {
    const value = current?.type === "bool" ? (current.value ? "yes" : "no") : "any";
    return (
      <label className="qm-col">
        <span>{column.label}</span>
        <select value={value} onChange={(e) => put(e.target.value === "any" ? null
          : { key: column.key, label: column.label, type: "bool", value: e.target.value === "yes" })}>
          <option value="any">any</option>
          <option value="yes">yes</option>
          <option value="no">no</option>
        </select>
      </label>
    );
  }
  if (column.type === "number") {
    const range = current?.type === "number" ? current.value : null;
    const set = (i: 0 | 1, raw: string) => {
      const n = raw === "" ? null : Number(raw);
      const next: [number, number] = range ? [...range] as [number, number] : [-1e9, 1e9];
      if (n === null || Number.isNaN(n)) next[i] = i === 0 ? -1e9 : 1e9;
      else next[i] = n;
      put(next[0] <= -1e9 && next[1] >= 1e9 ? null : { key: column.key, label: column.label, type: "number", value: next });
    };
    return (
      <label className="qm-col">
        <span>{column.label}</span>
        <input type="number" placeholder="min" value={range && range[0] > -1e9 ? range[0] : ""} onChange={(e) => set(0, e.target.value)} />
        <input type="number" placeholder="max" value={range && range[1] < 1e9 ? range[1] : ""} onChange={(e) => set(1, e.target.value)} />
      </label>
    );
  }
  return (
    <label className="qm-col">
      <span>{column.label}</span>
      <input
        value={text}
        placeholder="any of: a, b…"
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          const values = text.split(",").map((v) => v.trim()).filter(Boolean);
          put(values.length ? { key: column.key, label: column.label, type: "text", value: values } : null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
    </label>
  );
}
